/**
 * Uyarlanabilir merdiven.
 *
 * Ürünün çıktısı uydurma bir 0–100 puanı değil, ÖLÇÜLEBİLİR BİR EŞİK:
 * kaç basamak, hangi zorluk seviyesi. Merdiven bunu bulur.
 *
 * Kural: n ardışık doğruda zorlaşır, m yanlışta kolaylaşır. 1-yukarı /
 * 2-aşağı kuralı yakınsadığı nokta yaklaşık %70,7 doğruluk — yani çocuk
 * ne sıkılacak kadar kolay ne de vazgeçecek kadar zor bir seviyede kalır.
 *
 * Eşik tahmini son dönüş noktalarının (reversal) ortalamasıdır. Dönüş
 * noktası = yönün değiştiği an; ilk birkaç dönüş atılır, çünkü başlangıç
 * seviyesinin etkisini taşır.
 */

export interface StaircaseConfig {
  start: number
  min: number
  max: number
  /** Zorlaşmak için kaç ardışık doğru gerekir */
  down: number
  /** Kolaylaşmak için kaç yanlış gerekir */
  up: number
  stepUp: number
  stepDown: number
  /** Kaç dönüş noktasından sonra dursun */
  reversalsToStop: number
  maxTrials: number
  /** Eşik için son kaç dönüş noktası ortalanır (çift sayı olmalı) */
  thresholdReversals: number
  /** Baştaki kaç dönüş atılır */
  dropFirstReversals: number
}

export const DEFAULT_STAIRCASE: StaircaseConfig = {
  start: 3,
  min: 1,
  max: 12,
  down: 2,
  up: 1,
  stepUp: 1,
  stepDown: 1,
  reversalsToStop: 8,
  maxTrials: 40,
  thresholdReversals: 6,
  dropFirstReversals: 2,
}

export class Staircase {
  private cfg: StaircaseConfig
  private current: number
  private consecutiveCorrect = 0
  private consecutiveWrong = 0
  /** +1 zorlaşıyor, -1 kolaylaşıyor, 0 henüz yön yok */
  private direction: 0 | 1 | -1 = 0
  private reversals: number[] = []
  private trialCount = 0
  private levelHistory: number[] = []

  constructor(cfg: Partial<StaircaseConfig> = {}) {
    this.cfg = { ...DEFAULT_STAIRCASE, ...cfg }
    if (this.cfg.thresholdReversals % 2 !== 0) {
      throw new Error('thresholdReversals çift olmalı: yukarı ve aşağı dönüşler dengelenmeli')
    }
    this.current = this.clamp(this.cfg.start)
  }

  get level(): number {
    return this.current
  }

  get trials(): number {
    return this.trialCount
  }

  get reversalCount(): number {
    return this.reversals.length
  }

  get finished(): boolean {
    return (
      this.reversals.length >= this.cfg.reversalsToStop || this.trialCount >= this.cfg.maxTrials
    )
  }

  /**
   * Bir denemenin sonucunu işler ve sonraki seviyeyi belirler.
   *
   * GEÇERSİZ DENEME MERDİVENE GİRMEZ: kare atlamış veya sekme arkaya düşmüş
   * bir deneme ne doğru ne yanlış sayılır, seviye değişmez. Aksi halde
   * teknik bir aksaklık çocuğun eşiğini aşağı çekerdi.
   */
  record(correct: boolean, valid = true): void {
    if (!valid) return

    this.trialCount++
    this.levelHistory.push(this.current)

    if (correct) {
      this.consecutiveCorrect++
      this.consecutiveWrong = 0
      if (this.consecutiveCorrect >= this.cfg.down) {
        this.consecutiveCorrect = 0
        this.step(+1)
      }
    } else {
      this.consecutiveWrong++
      this.consecutiveCorrect = 0
      if (this.consecutiveWrong >= this.cfg.up) {
        this.consecutiveWrong = 0
        this.step(-1)
      }
    }
  }

  private step(dir: 1 | -1): void {
    const next = this.clamp(this.current + (dir === 1 ? this.cfg.stepUp : -this.cfg.stepDown))

    // Uçta sıkışmak dönüş sayılmaz: seviye gerçekten değişmediyse yön de değişmedi
    if (next === this.current) return

    if (this.direction !== 0 && dir !== this.direction) {
      this.reversals.push(this.current)
    }
    this.direction = dir
    this.current = next
  }

  private clamp(v: number): number {
    return Math.min(this.cfg.max, Math.max(this.cfg.min, v))
  }

  /**
   * Eşik tahmini. Yeterli dönüş yoksa son seviyelerin ortalamasına düşer
   * ve bunu `fallback` ile bildirir — rapora "tahmin güvenilirliği düşük"
   * notu düşmek için gerekli.
   */
  threshold(): { value: number; reversalsUsed: number; fallback: boolean } {
    const usable = this.reversals.slice(this.cfg.dropFirstReversals)
    if (usable.length >= this.cfg.thresholdReversals) {
      const take = usable.slice(-this.cfg.thresholdReversals)
      return {
        value: mean(take),
        reversalsUsed: take.length,
        fallback: false,
      }
    }
    if (usable.length >= 2) {
      const even = usable.length % 2 === 0 ? usable : usable.slice(0, -1)
      return { value: mean(even), reversalsUsed: even.length, fallback: true }
    }
    const tail = this.levelHistory.slice(-Math.min(8, this.levelHistory.length))
    return {
      value: tail.length > 0 ? mean(tail) : this.current,
      reversalsUsed: 0,
      fallback: true,
    }
  }
}

function mean(xs: number[]): number {
  if (xs.length === 0) return 0
  let s = 0
  for (const x of xs) s += x
  return s / xs.length
}
